import React from 'react';
import * as Api from './services/api.js';
import { iniciarLoginMicrosoft, processarRedirect, logoutMicrosoft } from './services/msauth.js';
const { Icon, Avatar, Button, Logo, Dashboard, Monitor, Contratos, Detalhe, Alertas, Indices, Arquivos, Aditivos, Societarios, Alvaras, Certificados, Empresas, Usuarios, Login, Dialogs } = window;
/* ============================================================
   BOOKER — App shell (navegação, tema, roteamento)
   ============================================================ */
var { useState, useEffect, useRef, useMemo } = React;

function App() {
  const DB = window.DB;
  const [theme, setTheme] = useState(() => localStorage.getItem('bkr-theme') || 'light');
  const [auth, setAuth] = useState(() => { try { return JSON.parse(localStorage.getItem('bkr-auth') || 'null'); } catch (e) { return null; } });
  const [route, setRoute] = useState({ screen: 'dashboard', param: null });
  const [menu, setMenu] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('bkr-theme', theme);
  }, [theme]);

  // Sincroniza os índices econômicos com a API do Banco Central ao entrar.
  // Ao concluir, força um re-render para refletir os valores ao vivo nas telas.
  const [sync, setSync] = useState(0);
  useEffect(() => {
    let vivo = true;
    DB.atualizarIndices(false, Date.now()).finally(() => { if (vivo) setSync(s => s + 1); });
    return () => { vivo = false; };
  }, []);

  // Carrega/recarrega os dados REAIS dos contratos (do backend) e re-renderiza.
  const recarregarDados = React.useCallback(async () => {
    try {
      const d = await Api.apiContractsData();
      if (d && Array.isArray(d.contratos)) { DB.carregarReais(d.clientes, d.contratos); setSync(s => s + 1); }
    } catch (e) {
      // sessão expirada/inválida → volta para o login (em vez de ficar sem dados)
      if (/\b401\b|sess/i.test(String(e && e.message))) {
        localStorage.removeItem('bkr-auth'); localStorage.removeItem('bkr-token'); setAuth(null);
      } else { throw e; }
    }
  }, []);
  useEffect(() => { window.recarregarContratos = recarregarDados; }, [recarregarDados]);
  useEffect(() => {
    if (!auth) return;
    let vivo = true;
    recarregarDados().catch(() => {});
    return () => { vivo = false; };
  }, [auth]);

  const login = (acc) => { localStorage.setItem('bkr-auth', JSON.stringify(acc)); setAuth(acc); };

  // Login Microsoft via redirect: o clique manda a página para a Microsoft.
  const [processandoLogin, setProcessandoLogin] = useState(() => /code=|error=|state=/.test(window.location.hash));
  const [loginErro, setLoginErro] = useState('');
  const entrar = async () => { setLoginErro(''); await iniciarLoginMicrosoft(); };
  // Acesso de emergência (break-glass): e-mail + senha do admin do .env, para
  // quando o SSO Microsoft estiver fora ou ainda não houver admin para aprovar.
  const entrarSenha = async (email, senha) => {
    setLoginErro('');
    const { token, user } = await Api.apiLogin(email, senha);
    localStorage.setItem('bkr-token', token);
    login(user);
  };
  // Ao voltar do redirect, troca o ID token por um JWT do sistema.
  useEffect(() => {
    let vivo = true;
    processarRedirect()
      .then(async (r) => {
        if (!vivo || !r) return;
        const { token, user } = await Api.apiLoginMicrosoft(r.idToken);
        localStorage.setItem('bkr-token', token);
        login(user);
      })
      .catch(e => { if (vivo) setLoginErro(e.message || 'Falha no login.'); })
      .finally(() => { if (vivo) setProcessandoLogin(false); });
    return () => { vivo = false; };
  }, []);

  const logout = () => {
    logoutMicrosoft().catch(() => {});
    localStorage.removeItem('bkr-auth'); localStorage.removeItem('bkr-token');
    setAuth(null); setRoute({ screen: 'dashboard', param: null }); setMenu(false);
  };

  const navigate = (screen, param = null) => {
    setRoute({ screen, param });
    // registra a navegação no histórico do navegador (botão Voltar anda entre telas)
    window.history.pushState({ bkrScreen: screen, bkrParam: param }, '');
    window.scrollTo({ top: 0 });
  };

  // Integra o botão Voltar/Avançar do navegador com a navegação interna do app.
  useEffect(() => {
    window.history.replaceState({ bkrScreen: route.screen, bkrParam: route.param }, '');
    const onPop = (e) => {
      const s = e.state || {};
      if (s.bkrScreen) { setRoute({ screen: s.bkrScreen, param: s.bkrParam ?? null }); window.scrollTo({ top: 0 }); }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []); // eslint-disable-line

  // alert count for badge
  const alertCount = useMemo(() => {
    let n = 0;
    DB.contratosComCliente().filter(k => k.status !== 'Encerrado').forEach(k => {
      const vi = k.valorInicial;
      if (vi == null || vi <= 0 || (vi < 100 && !k.valorConferido)) n++;  // valor a revisar
      if (k.dias >= 0 && k.dias <= 30) n++;               // renova em ≤30 dias
    });
    return n;
  }, [sync]);

  // RBAC (UX): o backend é a autoridade; aqui só ajustamos a interface.
  // (hooks ANTES de qualquer return condicional — Regras de Hooks)
  const papel = (auth && auth.papel) || undefined;
  const ehAdmin = papel === 'admin';
  const podeEditar = papel === 'admin' || papel === 'operador' || !papel; // admin/operador editam; backend valida
  // setado SÍNCRONO (não em effect): garante que as telas-filhas já leem o valor certo na 1ª render
  window.ehAdmin = () => ehAdmin;
  window.podeEditar = () => podeEditar;
  window.papelAtual = () => papel;
  // Badge de "pedidos de acesso pendentes" no menu Acessos (só admin).
  const [pendentes, setPendentes] = useState(0);
  function recarregarPendentes() {
    if (papel === 'admin') window.Api.apiAcessosPendentes().then(d => setPendentes(d.pendentes || 0)).catch(() => {});
    else setPendentes(0);
  }
  window.recarregarPendentes = recarregarPendentes;
  useEffect(() => { recarregarPendentes(); }, [papel]);
  useEffect(() => {
    if (auth && !auth.papel) {
      window.Api.apiMe().then((u) => {
        if (u && u.papel) { const a = { ...auth, papel: u.papel }; localStorage.setItem('bkr-auth', JSON.stringify(a)); setAuth(a); }
      }).catch(() => {});
    }
  }, [auth]);

  if (!auth) {
    if (processandoLogin) return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, background: 'var(--bg)' }}>
        <span style={{ width: 42, height: 42, border: '4px solid var(--surface-inset)', borderTopColor: 'var(--booker-orange)', borderRadius: 99, animation: 'spin .8s linear infinite' }} />
        <div style={{ color: 'var(--text-muted)', fontSize: 14, fontWeight: 600 }}>Conectando…</div>
      </div>
    );
    return <Login onSubmit={entrar} onEmergencia={entrarSenha} erro={loginErro} theme={theme} onToggleTheme={() => setTheme(t => t === 'light' ? 'dark' : 'light')} />;
  }

  const NAV = [
    { id: 'dashboard', label: 'Visão Executiva', icon: 'dashboard' },
    { id: 'monitor', label: 'Monitoramento', icon: 'refresh' },
    { id: 'arquivos', label: 'Contratos', icon: 'folder' },
    { id: 'contratos', label: 'Gestão de Contratos', icon: 'contratos' },
    { id: 'aditivos', label: 'Aditivos', icon: 'link' },
    { id: 'societarios', label: 'Quadro Societário', icon: 'building' },
    { id: 'alvaras', label: 'Alvarás', icon: 'doc' },
    { id: 'certificados', label: 'Certificados', icon: 'file' },
    { id: 'empresas', label: 'Empresas Booker', icon: 'building' },
    { id: 'indices', label: 'Índices', icon: 'trend' },
    { id: 'alertas', label: 'Alertas', icon: 'alertas', badge: alertCount },
    ...(ehAdmin ? [{ id: 'usuarios', label: 'Acessos', icon: 'user', badge: pendentes, badgeCor: 'var(--booker-orange)' }] : []),
  ];

  let screen;
  if (route.screen === 'dashboard') screen = <Dashboard onNavigate={navigate} sync={sync} />;
  else if (route.screen === 'monitor') screen = <Monitor onNavigate={navigate} account={auth} />;
  else if (route.screen === 'contratos') screen = <Contratos onNavigate={navigate} initialFilter={route.param} sync={sync} />;
  else if (route.screen === 'detalhe') screen = <Detalhe contratoId={route.param} onNavigate={navigate} onSaved={recarregarDados} />;
  else if (route.screen === 'arquivos') screen = <Arquivos onNavigate={navigate} />;
  else if (route.screen === 'aditivos') screen = <Aditivos onNavigate={navigate} sync={sync} />;
  else if (route.screen === 'societarios') screen = <Societarios onNavigate={navigate} />;
  else if (route.screen === 'alvaras') screen = <Alvaras onNavigate={navigate} />;
  else if (route.screen === 'certificados') screen = <Certificados onNavigate={navigate} />;
  else if (route.screen === 'empresas') screen = <Empresas onNavigate={navigate} />;
  else if (route.screen === 'indices') screen = <Indices onNavigate={navigate} />;
  else if (route.screen === 'alertas') screen = <Alertas onNavigate={navigate} sync={sync} />;
  else if (route.screen === 'usuarios') screen = ehAdmin
    ? <Usuarios account={auth} />
    : <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}><Icon name="alert" size={28} style={{ color: 'var(--booker-orange)' }} /><div style={{ marginTop: 10, fontWeight: 700, color: 'var(--text-strong)' }}>Sem permissão</div><div style={{ fontSize: 13 }}>Só administradores acessam a gestão de usuários.</div></div>;

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <aside style={{ width: 'var(--sidebar-w)', flexShrink: 0, background: 'var(--surface)', borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', position: 'sticky', top: 0, height: '100vh' }}>
        <div style={{ padding: '22px 22px 18px' }}>
          <Logo theme={theme} h={60} />
        </div>
        <nav style={{ padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '.06em', padding: '8px 12px 6px' }}>Monitoramento</div>
          {NAV.map(n => {
            const active = route.screen === n.id || (n.id === 'contratos' && route.screen === 'detalhe');
            return (
              <button key={n.id} onClick={() => navigate(n.id)} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: '11px 12px', borderRadius: 'var(--r-sm)', border: 'none',
                background: active ? 'var(--surface-inset)' : 'transparent', color: active ? 'var(--text-strong)' : 'var(--text-muted)',
                fontWeight: active ? 700 : 600, fontSize: 14, textAlign: 'left', position: 'relative', transition: 'all .14s' }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'var(--surface-2)'; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
                {active && <span style={{ position: 'absolute', left: -14, top: 9, bottom: 9, width: 3, borderRadius: 99, background: 'var(--booker-yellow)' }} />}
                <Icon name={n.icon} size={19} style={{ color: active ? 'var(--booker-orange)' : 'inherit' }} />
                <span style={{ flex: 1 }}>{n.label}</span>
                {n.badge > 0 && <span style={{ background: n.badgeCor || 'var(--danger)', color: '#fff', fontSize: 11, fontWeight: 800, minWidth: 20, height: 20, borderRadius: 99, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px' }}>{n.badge}</span>}
              </button>
            );
          })}
        </nav>

        {/* O perfil do usuário foi movido para a topbar (canto superior direito). */}
      </aside>

      {/* Main */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Topbar */}
        <header style={{ height: 'var(--topbar-h)', borderBottom: '1px solid var(--border)', background: 'var(--surface)',
          display: 'flex', alignItems: 'center', gap: 16, padding: '0 28px', position: 'sticky', top: 0, zIndex: 20 }}>
          <div style={{ position: 'relative', flex: 1, maxWidth: 420 }}>
            <Icon name="search" size={17} style={{ position: 'absolute', left: 13, top: 11, color: 'var(--text-muted)' }} />
            <input placeholder="Buscar contratos, clientes, CNPJ…" onFocus={() => navigate('contratos')}
              style={{ width: '100%', padding: '10px 12px 10px 40px', borderRadius: 'var(--r-sm)', border: '1px solid var(--border)',
                background: 'var(--surface-2)', color: 'var(--text-strong)', fontSize: 13.5, outline: 'none' }} />
          </div>
          <div style={{ flex: 1 }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setTheme(t => t === 'light' ? 'dark' : 'light')} title="Alternar tema"
              style={{ width: 40, height: 40, borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', background: 'var(--surface)',
                color: 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name={theme === 'light' ? 'moon' : 'sun'} size={19} />
            </button>
            <button onClick={() => navigate('alertas')} title="Alertas" style={{ position: 'relative', width: 40, height: 40, borderRadius: 'var(--r-sm)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="bell" size={19} />
              {alertCount > 0 && <span style={{ position: 'absolute', top: 7, right: 8, width: 8, height: 8, borderRadius: 99, background: 'var(--danger)', border: '2px solid var(--surface)' }} />}
            </button>

            {/* Perfil do usuário — movido do rodapé da sidebar para o topo à direita. */}
            <div style={{ position: 'relative' }}>
              <button onClick={() => setMenu(m => !m)} title={auth.email} style={{ display: 'flex', alignItems: 'center', gap: 9, height: 40, padding: '0 10px 0 5px', background: 'transparent', border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <span style={{ width: 30, height: 30, borderRadius: 10, background: 'var(--warn-bg)', color: 'var(--booker-orange)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon name="user" size={18} />
                </span>
                <span style={{ maxWidth: 150, fontSize: 13, fontWeight: 700, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{auth.nome}</span>
                <Icon name="chevronD" size={15} style={{ color: 'var(--text-faint)', transform: menu ? 'rotate(180deg)' : 'none', transition: 'transform .15s', flexShrink: 0 }} />
              </button>
              {menu && (
                <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', width: 'max-content', minWidth: 230, maxWidth: 360, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r)', boxShadow: 'var(--shadow-lg)', overflow: 'hidden', animation: 'popIn .18s ease both', zIndex: 40 }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-strong)' }}>{auth.nome}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3, whiteSpace: 'nowrap' }}>{auth.email}</div>
                  </div>
                  <button onClick={logout} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', border: 'none', background: 'transparent', color: 'var(--danger)', fontWeight: 600, fontSize: 13.5, textAlign: 'left', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <Icon name="external" size={16} /> Sair da conta
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Conteúdo */}
        <main style={{ padding: '28px', maxWidth: 1320, width: '100%', margin: '0 auto', flex: 1 }}>
          {screen}
        </main>
      </div>
      <Dialogs />
    </div>
  );
}

export default App;
